//registerForm.js
import { useState, useEffect, useContext } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';  // For redirecting to login page
import { Box, Field, Input, Button, VStack, Text, Link, Heading } from "@chakra-ui/react";
import { authTexts } from "../i18n/login";
import { LanguageContext } from "../contexts/LanguageContext";
import { resumePendingReserve } from "../services/pendingReserve";
const API_BASE_URL = process.env.REACT_APP_API_URL;

const labelStyles = {
  fontFamily: "space",
  fontSize: "xs",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "gray.600",
};

const inputStyles = {
  bg: "white",
  borderColor: "gray.300",
  color: "gray.900",
  _placeholder: { color: "gray.400" },
  _focusVisible: { borderColor: "gray.900", boxShadow: "none" },
};

const RegisterForm = ({ setIsLoggedIn }) => {
  const { language } = useContext(LanguageContext);
  const t = authTexts[language] || authTexts["et"];
  // Identity convergence: the email IS the account name (shared with the shop)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',  // Add confirmPassword state
  });
  const [errors, setErrors] = useState({});
  const [passwordError, setPasswordError] = useState('');  // State for password mismatch
  const [successMessage, setSuccessMessage] = useState('');
  const [csrfToken, setCsrfToken] = useState('');
  const navigate = useNavigate();  // React Router's navigate function

  // Fetch CSRF token from Django backend explicitly
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/csrf/`, {
          credentials: 'include',  // Ensures cookies (CSRF token) are included
        });
        const data = await response.json();
        setCsrfToken(data.csrfToken);  // Store token in state
      } catch (error) {
        console.error('Failed to fetch CSRF token', error);
      }
    };

    fetchCsrfToken();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Plausibility check before hitting the backend
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setErrors({ email: t.invalidEmail });
      return;
    }
    setErrors({});

    // Check if passwords match before submitting
    if (formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    setPasswordError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/register/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,  // Include CSRF token
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
        credentials: 'include',  // Include cookies (for CSRF)
      });

      if (response.ok) {
        // Backend auto-logs the new user in (JWT cookies already set)
        const data = await response.json();
        setSuccessMessage(t.registerSuccess);
        if (setIsLoggedIn) setIsLoggedIn(true);

        // Continue with a remembered instrument selection when there is one,
        // otherwise follow the backend redirect (new users → /profile)
        const resumed = await resumePendingReserve(navigate);
        if (!resumed) navigate(data.redirect || '/profile');

      } else {
        const errorData = await response.json();
        setErrors(errorData);
      }
    } catch (error) {
      console.error('Error registering user', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  return (
    <Box minH="calc(100vh - 4rem)" display="flex" justifyContent="center" alignItems="flex-start" bg="gray.50" pt={{ base: 10, md: 16 }} px={4}>
      <Box
        w={{ base: "100%", sm: "420px" }}
        bg="white"
        p={{ base: 6, md: 10 }}
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <form onSubmit={handleSubmit}>
          <VStack gap={5} align="stretch">
            <Box textAlign="center" mb={2}>
              <Text
                fontFamily="space"
                fontSize="xs"
                letterSpacing="0.3em"
                textTransform="uppercase"
                color="gray.500"
                mb={2}
              >
                akordion.ee
              </Text>
              <Heading fontFamily="heading" fontWeight="500" fontSize="3xl" color="gray.900">
                {t.register}
              </Heading>
            </Box>

            <Field.Root invalid={!!errors.email}>
              <Field.Label {...labelStyles}>{t.email}</Field.Label>
              <Input
                {...inputStyles}
                type="email"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={t.enteremail}
              />
              {errors.email && <Field.ErrorText>{errors.email}</Field.ErrorText>}
            </Field.Root>

            <Field.Root invalid={!!errors.password}>
              <Field.Label {...labelStyles}>{t.password}</Field.Label>
              <Input
                {...inputStyles}
                type="password"
                name="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder={t.enterpassword}
              />
              {errors.password && <Field.ErrorText>{errors.password}</Field.ErrorText>}
            </Field.Root>

            <Field.Root invalid={!!passwordError}>
              <Field.Label {...labelStyles}>{t.confirmPassword}</Field.Label>
              <Input
                {...inputStyles}
                type="password"
                name="confirmPassword"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder={t.confirmPassword}
              />
              {passwordError && <Field.ErrorText>{passwordError}</Field.ErrorText>}
            </Field.Root>

            <Button
              type="submit"
              w="full"
              size="lg"
              bg="#09090b"
              color="white"
              borderRadius="sm"
              fontFamily="space"
              fontSize="xs"
              fontWeight="500"
              letterSpacing="0.14em"
              textTransform="uppercase"
              _hover={{ bg: "gray.800" }}
            >
              {t.register}
            </Button>

            {successMessage && (
              <Text color="green.600" fontSize="sm" textAlign="center">
                {successMessage}
              </Text>
            )}

            <Text fontSize="sm" color="gray.600" textAlign="center">
              {t.haveAccount}{" "}
              <Link
                as={RouterLink}
                to="/login"
                color="gray.900"
                fontWeight="medium"
                textDecoration="underline"
                textUnderlineOffset="3px"
                _hover={{ color: "gray.600" }}
              >
                {t.signInHere}
              </Link>
            </Text>
          </VStack>
        </form>
      </Box>
    </Box>
  );
};

export default RegisterForm;
